import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Table, Input as AntInput, Button as AntButton, Dropdown, Checkbox, Space, Drawer, Switch, message, DatePicker } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getOrderStatusList, getOrderStatusListByProductNo, getGroupProfitDetail, getGroupEventInfo, saveGroupDepartureConfirm, saveGroupScheduleConfirm, getCities, getManagers, getProductTypes } from '../services/apiService';
import { getProductDetails, saveProductBasicInfo, getHotelBasicInfo } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import ProductSettingsModal from '../components/ProductSettingsModal';
import ProductSwitcherModal from '../components/ProductSwitcherModal';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import AvailableDatesModal from '../components/AvailableDatesModal';
import HotelReservationModal from '../components/HotelReservationModal';
import GeneralReservationModal from '../components/GeneralReservationModal';

/**
 * 조회 기준: '1' 예약일, '2' 행사일.
 *
 * 단품은 행사일이 없다. 재고·가격을 먼 미래 하루(2036-12-31)에 몰아 저장하는 구조라,
 * 행사일로 거르면 주문이 전부 기본 조회 범위 밖으로 밀려나 목록이 늘 비어 보였다.
 * 단품에서 의미 있는 날짜는 "언제 주문이 들어왔나" 하나뿐이라 예약일로 본다.
 * (투어·호텔 주문 화면은 행사일 기준 그대로다)
 */
const SEARCH_BASIS_RESERVED = '1';


const columns = [
  { key: '주문번호', label: '주문번호', type: 'string' },
  { key: '수량', label: '수량', type: 'number' },
  { key: '행사시작일', label: '행사시작일', type: 'date' },
  { key: '행사종료일시', label: '행사종료일', type: 'date' },
  { key: '행사기간', label: '행사기간', type: 'string' },
  { key: '예약일', label: '주문일', type: 'date' },
  { key: '예약상태2', label: '예약상태2', type: 'string' },
  { key: '여행객수', label: '여행객수', type: 'number' },
  { key: '예약고객명', label: '주문 고객명', type: 'string' },
  { key: '예약상태3', label: '예약상태3', type: 'string' },
  { key: '상품금액', label: '상품금액', type: 'number' },
  { key: '예상수익', label: '예상수익', type: 'number' },
  { key: '예약자휴대폰', label: '예약자휴대폰', type: 'string' },
  { key: '입금액', label: '입금액', type: 'number' },
  { key: '입금상태', label: '입금상태', type: 'string' },
  { key: '판매처', label: '판매처', type: 'string' },
  { key: '예약경로', label: '예약경로', type: 'string' },
];

function formatNumberWithCommas(value) {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('ko-KR');
}

function formatCellValue(key, value) {
  if (key === '상품금액' || key === '예상수익'|| key === '입금액') {
    return formatNumberWithCommas(value);
  }
  if (key === '행사종료일시') {
    if (value === null || value === undefined || value === '') return '';
    const s = String(value).trim();
    if (s.includes(' ')) return s.split(' ')[0];
    if (s.includes('T')) return s.split('T')[0];
    const m = s.match(/^\d{4}[./-]\d{1,2}[./-]\d{1,2}/);
    if (m) return m[0];
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return s;
  }
  return value ?? '';
}

export default function GeneralOrderListPage({ afterHeader }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { productCode: encodedProductCode } = useParams();
  const productCode = decodeURIComponent(encodedProductCode || '');

  const queryProductNo = useMemo(() => {
    const navProductNo = location?.state?.productNo;
    if (navProductNo != null && String(navProductNo).trim() !== '') return Number(navProductNo);
    const code = (productCode || '').trim();
    if (code && code !== '-' && /^\d+$/.test(code)) return Number(code);
    return undefined;
  }, [location?.state?.productNo, productCode]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [dateRange, setDateRange] = useState(() => [dayjs().subtract(1, 'year'), dayjs().add(1, 'year')]);
  const [lastQueryRange, setLastQueryRange] = useState(() => ({
    start: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
    end: dayjs().add(1, 'year').format('YYYY-MM-DD'),
  }));
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [colFilters, setColFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [density, setDensity] = useState('compact');
  const [hideCancelled, setHideCancelled] = useState(true);
  const [pageSize, setPageSize] = useState(200);
  const [visibleCols, setVisibleCols] = useState(() => {
    const hiddenKeys = new Set(['예약상태3', '행사시작일', '행사종료일시', '행사기간', '여행객수']);
    return new Set(columns.filter(c => !hiddenKeys.has(c.key)).map(c => c.key));
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  // 드래그 가능한 컬럼 순서 상태 (localStorage 유지)
  const LS_COL_ORDER_KEY = 'general_orders_col_order_v1';
  const defaultColOrder = useMemo(() => columns.map(c => c.key), []);
  const [colOrder, setColOrder] = useState(() => {
    try {
      const raw = window.localStorage.getItem(LS_COL_ORDER_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(saved) || saved.length === 0) return defaultColOrder;
      const setSaved = new Set(saved);
      const merged = saved.filter(k => defaultColOrder.includes(k));
      defaultColOrder.forEach(k => { if (!setSaved.has(k)) merged.push(k); });
      return merged;
    } catch {
      return defaultColOrder;
    }
  });
  const [draggingColKey, setDraggingColKey] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [groupProfit, setGroupProfit] = useState(null);
  const [groupProfitLoading, setGroupProfitLoading] = useState(false);
  const [groupProfitError, setGroupProfitError] = useState(null);
  const [groupEvent, setGroupEvent] = useState(null);
  const [groupEventLoading, setGroupEventLoading] = useState(false);
  const [groupEventError, setGroupEventError] = useState(null);
  const [departureConfirm, setDepartureConfirm] = useState(false);
  const [scheduleConfirm, setScheduleConfirm] = useState(false);
  const [groupEventSaveLoading, setGroupEventSaveLoading] = useState(false);

  const openGroupDetail = async (groupRecord) => {
    if (!groupRecord || !groupRecord._isGroup) return;
    const items = [];
    if (groupRecord._firstChild) items.push(groupRecord._firstChild);
    if (Array.isArray(groupRecord.children)) items.push(...groupRecord.children);
    const first = items[0];
    const saleNo = first?.판매번호 ?? first?.주문판매번호;
    setGroupDetail({ groupKey: groupRecord.groupKey, groupName: groupRecord.groupName, count: groupRecord.count, items, saleNo });
    setGroupProfit(null); setGroupProfitError(null);
    if (saleNo != null) {
      try { setGroupProfitLoading(true); const data = await getGroupProfitDetail({ saleNo }); setGroupProfit(data || null); }
      catch (e) { console.error('Failed to fetch group profit detail', e); setGroupProfitError(e?.message || '단체 손익 정보를 불러오지 못했습니다.'); }
      finally { setGroupProfitLoading(false); }
    }
    setGroupEvent(null); setGroupEventError(null);
    if (saleNo != null) {
      try { setGroupEventLoading(true); const info = await getGroupEventInfo({ saleNo }); setGroupEvent(info || null); setDepartureConfirm(!!info?.출발확정); setScheduleConfirm(!!info?.일정확정); }
      catch (e) { console.error('Failed to fetch group event info', e); setGroupEventError(e?.message || '단체 정보를 불러오지 못했습니다.'); }
      finally { setGroupEventLoading(false); }
    }
  };

  const openOrderDetail = (row) => {
    if (!row || row._isGroup) return;
    const orderNo = row?.주문번호 || row?._groupKey || row?.orderNo;
    if (!orderNo) return;
    const saleNo = row?.판매번호 ?? row?.주문판매번호 ?? null;
    const productName = row?.판매상품명 ?? derivedProductName ?? null;
    navigate(`/reservation-detail/${orderNo}`, { state: { saleNo, productName } });
  };

  const containerRef = useRef(null);
  const topScrollRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(520);

  const getColType = (key) => columns.find((c) => c.key === key)?.type || 'string';
  const parseNumber = (v) => { if (v === null || v === undefined || v === '') return null; const num = Number(String(v).replace(/,/g, '')); return Number.isFinite(num) ? num : null; };
  const parseDate = (v) => { if (v === null || v === undefined || v === '') return null; const s = String(v).trim(); let dateStr = s; if (s.includes(' ')) dateStr = s.split(' ')[0]; if (dateStr.includes('T')) dateStr = dateStr.split('T')[0]; const d = new Date(dateStr); return isNaN(d.getTime()) ? null : d; };

  const isCancelledRow = (record) => {
    if (!record) return false;
    const target = record._isGroup ? record._firstChild : record;
    const text = (target?.예약상태2 || '').toString();
    return (text.includes('취소완료') || text.includes('취소확정') || text.includes('취소 접수') || text.includes('취소접수'));
  };
  function compareRows(a, b) {
    if (!sortKey) return 0;
    const type = getColType(sortKey);
    const av = a?.[sortKey]; const bv = b?.[sortKey];
    let cmp = 0;
    if (type === 'number') { const na = parseNumber(av); const nb = parseNumber(bv); if (na === null && nb === null) cmp = 0; else if (na === null) cmp = -1; else if (nb === null) cmp = 1; else cmp = na - nb; }
    else if (type === 'date') { const da = parseDate(av); const db = parseDate(bv); const ta = da ? da.getTime() : null; const tb = db ? db.getTime() : null; if (ta === null && tb === null) cmp = 0; else if (ta === null) cmp = -1; else if (tb === null) cmp = 1; else cmp = ta - tb; }
    else { const sa = (av ?? '').toString(); const sb = (bv ?? '').toString(); cmp = sa.localeCompare(sb, 'ko'); }
    return sortDir === 'asc' ? cmp : -cmp;
  }
  const toggleSort = (key) => { setSortKey((prevKey) => { if (prevKey !== key) { setSortDir('asc'); return key; } setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc')); return key; }); };
  const onFilterChange = (key, value) => { setColFilters((prev) => ({ ...prev, [key]: value })); };
  const visibleColumns = useMemo(() => {
    const filtered = columns.filter(c => visibleCols.has(c.key));
    const idxOf = (k) => { const i = colOrder.indexOf(k); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
    return filtered.sort((a, b) => idxOf(a.key) - idxOf(b.key));
  }, [visibleCols, colOrder]);

  const persistColOrder = (order) => { try { window.localStorage.setItem(LS_COL_ORDER_KEY, JSON.stringify(order)); } catch {} };
  const moveColBefore = (sourceKey, targetKey) => {
    setColOrder(prev => {
      if (!sourceKey || !targetKey || sourceKey === targetKey) return prev;
      const next = prev.slice();
      const sIdx = next.indexOf(sourceKey);
      const tIdx = next.indexOf(targetKey);
      if (sIdx === -1 || tIdx === -1) return prev;
      next.splice(sIdx, 1);
      next.splice(tIdx, 0, sourceKey);
      persistColOrder(next);
      return next;
    });
  };

  const loadData = async () => {
    try {
      setLoading(true); setError(null);
      let startDate = Array.isArray(dateRange) && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
      let endDate = Array.isArray(dateRange) && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;
      if (!startDate || !endDate) {
        // 날짜를 지웠을 때의 기본값은 화면 초기값과 같아야 한다. 예전에는 여기만 "오늘~1년 뒤"라
        // 초기 화면과 [조회] 결과가 달랐다.
        startDate = startDate || dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        endDate = endDate || dayjs().add(1, 'year').format('YYYY-MM-DD');
      }

      const list = queryProductNo
        ? await getOrderStatusListByProductNo({ startDate, endDate, searchBasis: SEARCH_BASIS_RESERVED, productNo: queryProductNo })
        : await getOrderStatusList({ startDate, endDate, searchBasis: SEARCH_BASIS_RESERVED });
      setLastQueryRange({ start: startDate, end: endDate });
      setRows(Array.isArray(list) ? list : []);
    } catch (e) { console.error('Failed to fetch order list', e); setError(e?.message || '주문 내역을 불러오지 못했습니다.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .child-row td:first-child { border-left: 4px solid #e5e7eb !important; }
      .child-row { background-color: rgb(var(--gray-50)); }
      .ant-table-row-expand-icon-cell { width: 48px !important; }
      .ant-table-tbody > tr.ant-table-row:hover > td { background-color: #eff6ff !important; }
      .ant-table-tbody > tr.ant-table-row.bg-blue-50 > td { background-color: #eff6ff !important; }
      .ant-table-tbody > tr.ant-table-row.bg-blue-50 { border-bottom: 2px solid #bfdbfe !important; }
      .ant-table-row-expand-icon { display: none !important; }
    `;
    document.head.appendChild(styleEl);
    return () => { document.body.style.overflow = prevOverflow; document.documentElement.style.overscrollBehavior = prevHtmlOverscroll; document.head.removeChild(styleEl); };
  }, []);

  const filtered = useMemo(() => {
    const data = Array.isArray(rows) ? rows : [];
    const code = (productCode || '').trim();
    const navName = (location?.state?.productName || '').trim();
    const navProductNo = location?.state?.productNo;
    if (navProductNo != null) {
      const noKeys = ['상품번호','ProductNo','productNo','판매상품번호'];
      const byNo = data.filter((r) => noKeys.some((k) => String(r?.[k] ?? '') === String(navProductNo)));
      if (byNo.length) return byNo;
    }
    // URL 파라미터가 숫자라면 상품번호로도 시도 (단품 페이지 딥링크 시 지원)
    if (code && /^\d+$/.test(code)) {
      const asNo = code;
      const noKeys = ['상품번호','ProductNo','productNo','판매상품번호'];
      const byUrlNo = data.filter((r) => noKeys.some((k) => String(r?.[k] ?? '') === String(asNo)));
      if (byUrlNo.length) return byUrlNo;
    }
    if (code && code !== '-') {
      const codeKeys = ['상품코드','ProductCode','productCode','판매상품코드'];
      const byCode = data.filter((r) => codeKeys.some((k) => String(r?.[k] ?? '').trim() === code));
      if (byCode.length) return byCode;
    }
    const nameToUse = navName;
    if (nameToUse) {
      const low = nameToUse.toLowerCase();
      const nameKeys = ['상품명','판매상품명','ProductName','productName'];
      const byName = data.filter((r) => nameKeys.some((k) => String(r?.[k] ?? '').trim().toLowerCase() === low));
      if (byName.length) return byName;
    }
    const hasCriteria = (navProductNo != null) || (code && code !== '-') || !!nameToUse;
    return hasCriteria ? [] : data;
  }, [rows, productCode, location?.state?.productName, location?.state?.productNo]);

  const uiFiltered = useMemo(() => {
    const base = hideCancelled ? filtered.filter((row) => !isCancelledRow(row)) : filtered;
    const activeKeys = Object.keys(colFilters).filter((k) => (colFilters[k] ?? '').toString().trim() !== '');
    if (activeKeys.length === 0 && (globalFilter ?? '').toString().trim() === '') return base;
    const lowered = Object.fromEntries(activeKeys.map((k) => [k, colFilters[k].toString().toLowerCase()]));
    const gq = (globalFilter ?? '').toString().trim().toLowerCase();
    return base.filter((row) => {
      const colPass = activeKeys.every((k) => {
        const raw = row?.[k]; if (raw === null || raw === undefined) return false; const display = (formatCellValue(k, raw) ?? '').toString().toLowerCase(); return display.includes(lowered[k]);
      });
      if (!colPass) return false;
      if (!gq) return true;
      return visibleColumns.some((c) => {
        const raw = row?.[c.key]; if (raw === null || raw === undefined) return false; const display = (formatCellValue(c.key, raw) ?? '').toString().toLowerCase(); return display.includes(gq);
      });
    });
  }, [filtered, hideCancelled, colFilters, globalFilter]);

  const sortedUiFiltered = useMemo(() => {
    if (!sortKey) return uiFiltered; const arr = uiFiltered.slice(); arr.sort(compareRows); return arr;
  }, [uiFiltered, sortKey, sortDir]);

  const grouped = useMemo(() => {
    const map = new Map(); for (const r of sortedUiFiltered) { const key = r?.행사단체번호 != null ? String(r.행사단체번호) : '기타'; if (!map.has(key)) map.set(key, []); map.get(key).push(r); }
    return Array.from(map.entries()).map(([groupKey, items]) => ({ groupKey, groupName: items[0]?.행사단체명 || '', items }));
  }, [sortedUiFiltered]);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const toggleGroupExpand = (groupKey) => { setExpandedRowKeys((prev) => { const key = `group-${groupKey}`; const set = new Set(prev); if (set.has(key)) set.delete(key); else set.add(key); return Array.from(set); }); };

  const antdColumns = useMemo(() => {
    const widthOverrides = { 주문번호: 120, 수량: 90, 예약경로: 140, 예약일: 130, 판매처: 160, 예약상태2: 120, 행사시작일: 130, 행사종료일시: 130, 행사기간: 100, 여행객수: 100, 예약고객명: 200, 예약상태3: 120, 상품금액: 120, 예상수익: 120, 예약자휴대폰: 200, 입금액: 120, 입금상태: 110 };
    const defaultByType = { number: 120, date: 130, string: 160 };
    const cols = [
      {
        title: '그룹', dataIndex: '_group', key: '_group', width: 220, align: 'center', ellipsis: false,
        render: (_, record) => {
          if (record._isGroup) {
            const isExpanded = expandedRowKeys.includes(`group-${record.groupKey}`);
            const commonStyle = { minHeight: '42px', display: 'flex', flexDirection: 'column', justifyContent: 'center' };
            if (hoveredRowKey === `group-${record.groupKey}`) {
              return (
                <div className="px-5 py-2.5" style={commonStyle}>
                  <div className="flex items-center gap-2 min-w-0">
                    {record._hasChildren && (
                      <button type="button" className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${isExpanded ? 'bg-blue-100 border-blue-300' : 'border border-gray-300'} text-gray-600 hover:bg-gray-100 shrink-0`} onClick={(e) => { e.stopPropagation(); toggleGroupExpand(record.groupKey); }} title={isExpanded ? '접기' : '펼치기'}>
                        {isExpanded ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                      </button>
                    )}
                    {!record._hasChildren && <div className="w-6"></div>}
                    <div className="flex flex-col min-w-0 flex-grow">
                      <div>
                        <span className="text-emerald-700 hover:underline cursor-pointer select-none" onClick={() => openGroupDetail(record)}>단체정보 상세보기</span>
                      </div>
                      <div className={`flex items-center ${!(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) ? 'justify-center' : ''} gap-1`}>
                        <span className="text-xs text-gray-700">(주문 <span className="text-red-700 tabular-nums">{record.count}</span>건)</span>
                        {(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) && (
                          <div className="flex gap-0.5 ml-1">
                            {record?._firstChild?.출발확정 && (<span className="inline-flex items-center rounded bg-blue-50 border border-blue-200 px-1 py-0 text-[10px] font-medium text-blue-700 whitespace-nowrap">출발</span>)}
                            {record?._firstChild?.일정확정 && (<span className="inline-flex items-center rounded bg-green-50 border border-green-200 px-1 py-0 text-[10px] font-medium text-green-700 whitespace-nowrap">일정</span>)}
                          </div>
                        )}
                      </div>
                      <div className="h-0.5"></div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="min-w-0 px-5 py-2.5" style={commonStyle}>
                <div className="flex items-center gap-2 min-w-0">
                  {record._hasChildren && (
                    <button type="button" className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${isExpanded ? 'bg-blue-100 border-blue-300' : 'border border-gray-300'} text-gray-600 hover:bg-gray-100 shrink-0`} onClick={(e) => { e.stopPropagation(); toggleGroupExpand(record.groupKey); }} title={isExpanded ? '접기' : '펼치기'}>
                      {isExpanded ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                    </button>
                  )}
                  {!record._hasChildren && <div className="w-6"></div>}
                  <div className="flex flex-col min-w-0 flex-grow">
                    <div className="text-sm font-medium text-gray-800">단체번호: <span className="text-emerald-700 tabular-nums">{record.groupKey}</span></div>
                    <div className={`mt-0.5 text-xs text-gray-700 flex items-center justify-center ${!(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) ? 'justify-center' : ''} gap-1`}>
                      <span>(주문 <span className="text-red-700 tabular-nums">{record.count}</span>건)</span>
                      {(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) && (
                        <div className="flex gap-0.5 ml-1">
                          {record?._firstChild?.출발확정 && (<span className="inline-flex items-center rounded bg-blue-50 border border-blue-200 px-1 py-0 text-[10px] font-medium text-blue-700 whitespace-nowrap">출발</span>)}
                          {record?._firstChild?.일정확정 && (<span className="inline-flex items-center rounded bg-green-50 border border-green-200 px-1 py-0 text-[10px] font-medium text-green-700 whitespace-nowrap">일정</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        },
        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px 20px', backgroundColor: 'rgb(var(--gray-100))', fontWeight: 600, textAlign: 'center' } }),
        onCell: () => ({ style: { whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'clip', maxWidth: '100%', padding: 0 } }),
      },
      ...visibleColumns.map((c) => ({
        title: (
          <div className="flex flex-col items-stretch gap-1">
            <div className="items-center justify-between gap-2">
              <span className="whitespace-nowrap px-1">{c.label}</span>
              <button type="button" className="text-[11px] px-1.5 py-0.5  rounded hover:bg-gray-100 text-gray-700" title="정렬" onClick={(e) => { e.stopPropagation(); toggleSort(c.key); }}>{sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</button>
            </div>
            <AntInput size="small" placeholder="" value={colFilters[c.key] ?? ''} onChange={(e) => onFilterChange(c.key, e.target.value)} allowClear onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} />
          </div>
        ),
        dataIndex: c.key, key: c.key, align: 'center', width: widthOverrides[c.key] ?? defaultByType[c.type] ?? 140, ellipsis: true,
        onHeaderCell: () => ({ 
          style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px 20px', backgroundColor: 'rgb(var(--gray-100))', fontWeight: 600, textAlign: 'center' },
          draggable: true,
          onDragStart: (e) => { try { e.dataTransfer.setData('text/plain', c.key); } catch {} setDraggingColKey(c.key); },
          onDragOver: (e) => { e.preventDefault(); },
          onDrop: (e) => { e.preventDefault(); const source = (() => { try { return e.dataTransfer.getData('text/plain') || draggingColKey; } catch { return draggingColKey; } })(); moveColBefore(source, c.key); setDraggingColKey(null); },
        }),
        onCell: () => ({ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', padding: '10px 20px' } }),
        render: (value, record) => {
          if (record._isGroup) {
            const first = record._firstChild; const v = first ? first[c.key] : undefined;
            if (c.key === '주문번호') {
              const groupKey = `group-${record.groupKey}`;
              if (hoveredRowKey === groupKey && first) {
                const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
                return (
                  <div style={cellStyle}>
                    <div>
                      <span className="text-emerald-700 hover:underline cursor-pointer select-none" onClick={() => openOrderDetail(first)}>주문내역 상세보기</span>
                      <div className="h-4"></div>
                    </div>
                  </div>
                );
              }
              const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
              return (
                <div style={cellStyle}>
                  <div>
                    <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''}`}>{formatCellValue(c.key, v)}</span>
                    <div className="h-4"></div>
                  </div>
                </div>
              );
            }
            const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
            return (
              <div style={cellStyle}>
                <div>
                  <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''} ${isCancelledRow(record) ? 'text-red-600 line-through' : ''}`}>{formatCellValue(c.key, v)}</span>
                  <div className="h-4"></div>
                </div>
              </div>
            );
          }
          if (c.key === '주문번호') {
            const rowKey = record.key || `${record.주문번호 || ''}-${record.예약일 || ''}`;
            const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
            if (hoveredRowKey === rowKey) {
              return (
                <div style={cellStyle}>
                  <div>
                    <span className="text-emerald-700 hover:underline cursor-pointer select-none" onClick={() => openOrderDetail(record)}>주문내역 상세보기</span>
                    <div className="h-4"></div>
                  </div>
                </div>
              );
            }
            return (
              <div style={cellStyle}>
                <div>
                  <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''} ${isCancelledRow(record) ? 'text-red-600 line-through' : ''}`}>{formatCellValue(c.key, value)}</span>
                  <div className="h-4"></div>
                </div>
              </div>
            );
          }
          const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
          return (
            <div style={cellStyle}>
              <div>
                <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''} ${isCancelledRow(record) ? 'text-red-600 line-through' : ''}`}>{formatCellValue(c.key, value)}</span>
                <div className="h-4"></div>
              </div>
            </div>
          );
        },
      })),
    ];
    return cols;
  }, [visibleColumns, hoveredRowKey]);

  const totalTableWidth = useMemo(() => antdColumns.reduce((sum, col) => sum + (col.width || 160), 0), [antdColumns]);

  const antdData = useMemo(() => {
    return grouped.map((g) => {
      const firstChild = g.items[0]; const rest = g.items.slice(1);
      const base = { _isGroup: true, groupKey: g.groupKey, groupName: g.groupName, count: g.items.length, _firstChild: firstChild, _hasChildren: rest.length > 0 };
      if (rest.length > 0) {
        return { ...base, children: rest.map((row, idx) => ({ ...row, key: `${g.groupKey}-${idx + 1}` })) };
      }
      return base;
    });
  }, [grouped]);

  useEffect(() => {
    setExpandedRowKeys((prev) => {
      const allowed = new Set(antdData.filter((r) => r._isGroup && r._hasChildren).map((r) => `group-${r.groupKey}`));
      if (!prev || prev.length === 0) { return Array.from(allowed); }
      return prev.filter((k) => allowed.has(k));
    });
  }, [antdData]);

  const recalcBodyHeight = () => {
    const el = containerRef.current; if (!el) return; const rect = el.getBoundingClientRect(); const viewportH = window.innerHeight || document.documentElement.clientHeight; const reserveBottom = 24 + 16 + 96; const available = Math.max(240, Math.floor(viewportH - rect.top - reserveBottom)); setBodyHeight(available);
  };

  useEffect(() => {
    recalcBodyHeight(); const onResize = () => recalcBodyHeight(); const onScroll = () => { const top = topScrollRef.current; const container = containerRef.current; if (!top || !container) return; top.scrollLeft = container.scrollLeft; };
    window.addEventListener('resize', onResize);
    const container = containerRef.current; if (container) container.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('resize', onResize); if (container) container.removeEventListener('scroll', onScroll); };
  }, []);

  // globalFilter state is declared above; no duplicate needed

  const [reserveDateModalOpen, setReserveDateModalOpen] = useState(false);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPriceNo, setSelectedPriceNo] = useState('');
  const [selectedProductTypeName, setSelectedProductTypeName] = useState('투어');
  const [modalProductTypeName, setModalProductTypeName] = useState('투어');

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [settingsProduct, setSettingsProduct] = useState(null);

  const derivedProductNo = location?.state?.productNo ?? undefined;
  const derivedProductName = location?.state?.productName ?? '';

  const openBasicSettings = async () => {
    try {
      setSettingsLoading(true);
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(derivedProductNo, companyNo);
      if (details) { setSettingsProduct(details); setIsSettingsOpen(true); } else { message.error('상품 상세 정보를 찾을 수 없습니다.'); }
    } catch (e) { message.error('상품 상세 정보를 불러오지 못했습니다.'); }
    finally { setSettingsLoading(false); }
  };

  const handleCloseModal = () => { setIsSettingsOpen(false); setSelectedProduct(null); };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setSettingsLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return false; }
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호;
      if (!productNo) return false;
      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []), getManagers().catch(() => []), getProductTypes().catch(() => []),
      ]);
      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;
      const original = selectedProduct || {};
      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr ? keywordStr.split(',').map(k => k.trim()).filter(Boolean) : [];
      const cityName = updatedProduct.도시 ?? original.도시; const cityItem = findByName(cities, cityName); const cityNo = cityItem?.코드 ?? original.도시번호;
      const productTypeName = updatedProduct.상품타입 ?? original.상품타입; const productTypeItem = findByName(productTypes, productTypeName); const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;
      const pdName = updatedProduct.상품담당 ?? original.상품담당; const rdName = updatedProduct.예약담당 ?? original.예약담당; const pdItem = findByName(managers, pdName); const rdItem = findByName(managers, rdName); const pdNo = pdItem?.코드 ?? original.상품담당번호; const rdNo = rdItem?.코드 ?? original.예약담당번호;
      const basicInfo = { ...original, 상품번호: productNo, 상품명: updatedProduct.상품명 ?? original.상품명, 도시번호: cityNo, 도시: cityName, 상품타입: productTypeName, 상품타입코드: productTypeCode, 상품코드: updatedProduct.상품코드 ?? original.상품코드, 상품코드룰사용: original.상품코드룰사용 ?? false, 상품담당: pdName, 상품담당번호: pdNo, 예약담당: rdName, 예약담당번호: rdNo, 디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당', 소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '', 소요: original.소요 ?? original.소요단위 ?? '', 소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0, 통화코드: original.통화코드 ?? '', 통화: original.통화 ?? '', 가이드타입: original.가이드타입 ?? '', 가이드타입코드: original.가이드타입코드 ?? '', 카테고리: original.카테고리 ?? '', 카테고리코드: original.카테고리코드 ?? '', 노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false), 키워드: keywordArr, 상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지 };
      await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(',') });
      alert('성공적으로 저장되었습니다.'); setIsSettingsOpen(false); setSelectedProduct(null);

      return true;
    } catch (e) { console.error('Failed to save product details:', e); alert('상품 정보 저장에 실패했습니다.'); return false; }
    finally { setSettingsLoading(false); }
  };

  const isQuoteDetailView = (location?.pathname || '').startsWith('/quote-detail');

  return (
    <div className="p-4">
      {isQuoteDetailView ? (
        <>
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg font-semibold text-gray-800 truncate max-w-[50vw]">{derivedProductName}</span>
                <span className="text-sm text-gray-600 shrink-0">(상품번호: {typeof derivedProductNo !== 'undefined' ? derivedProductNo : '-'})</span>
              </div>
              <div className="mt-2 mb-3 flex items-center gap-2">
                <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '기본설정' : '상품번호가 없어 기본설정을 열 수 없습니다'} onClick={openBasicSettings}>기본설정</button>
                <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '가격/재고' : '상품번호가 없어 이동할 수 없습니다'} onClick={() => derivedProductNo && navigate(`/general/price-stock/${derivedProductNo}`)}>가격/재고</button>
                <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '상세 페이지 설정' : '상품번호가 없어 이동할 수 없습니다'} onClick={() => derivedProductNo && navigate(`/general/detail-settings/${derivedProductNo}`)}>상세페이지 설정</button>
                <button type="button" className="px-2 py-1 text-xs rounded bg-primary-600 text-white hover:bg-blue-700" disabled={!derivedProductNo} title={derivedProductNo ? '신규 예약' : '상품번호가 없어 신규 예약을 진행할 수 없습니다'} onClick={() => setReserveDateModalOpen(true)}>신규 예약</button>
              </div>
            </div>
            <div className="space-x-2">
              <button className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50" onClick={() => navigate(-1)}>돌아가기</button>
            </div>
          </div>
          <div className="border-b border-gray-200 mb-4" />
          {afterHeader}
        </>
      ) : (
        <>
          <div className="flex items-center justify_between">
            <div className="flex items-center gap-3 min-w-0">
              <button type="button" onClick={() => setSwitcherOpen(true)} className="text-left text-lg font-semibold text-gray-800 truncate hover:underline max-w-[50vw]" title="다른 상품으로 이동">{derivedProductName}</button>
              <span className="text-sm text-gray-600 shrink-0">(상품번호: {typeof derivedProductNo !== 'undefined' ? derivedProductNo : '-'})</span>
            </div>
            <div className="space-x-2">
              <button className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50" onClick={() => navigate(-1)}>돌아가기</button>
            </div>
          </div>
          <ProductSwitcherModal open={switcherOpen} onClose={() => setSwitcherOpen(false)} currentProductId={derivedProductNo} onlyTour={true} onSelect={(id, row) => { setSwitcherOpen(false); navigate('/general/orders/-', { state: { productName: row?.상품명, productNo: id } }); }} />
          <div className="mt-2 mb-3 flex items-center gap-2">
            <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '기본설정' : '상품번호가 없어 기본설정을 열 수 없습니다'} onClick={openBasicSettings}>기본설정</button>
            <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '가격/재고' : '상품번호가 없어 이동할 수 없습니다'} onClick={() => derivedProductNo && navigate(`/general/price-stock/${derivedProductNo}`)}>가격/재고</button>
            <button type="button" className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100" disabled={!derivedProductNo} title={derivedProductNo ? '상세 페이지 설정' : '상품번호가 없어 이동할 수 없습니다'} onClick={() => derivedProductNo && navigate(`/general/detail-settings/${derivedProductNo}`)}>상세페이지 설정</button>
            <button type="button" className="px-2 py-1 text-xs rounded bg-primary-600 text-white hover:bg-blue-700" disabled={!derivedProductNo} title={derivedProductNo ? '신규 예약' : '상품번호가 없어 신규 예약을 진행할 수 없습니다'} onClick={() => setReserveDateModalOpen(true)}>신규 예약</button>
          </div>
          <div className="border-b border-gray-200 mb-4" />
          {afterHeader}
        </>
      )}

      <div className="mb-4 flex flex-wrap items_center gap-2">
        <AntInput value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="전체 검색" allowClear size="small" style={{ width: 240 }} />
        <span className="text-xs text-gray-500 self-center">예약일</span>
        <DatePicker.RangePicker size="small" value={dateRange} onChange={(v) => setDateRange(v)} allowClear placeholder={["예약 시작일", "예약 종료일"]} />
        <Checkbox checked={hideCancelled} onChange={(e) => setHideCancelled(e.target.checked)}>취소예약 숨기기</Checkbox>
        <AntButton size="small" type="primary" onClick={loadData}>조회</AntButton>
      </div>

      {antdData.length === 0 ? (
        <div className="mt-6 bg_white border border-gray-200 rounded-lg p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">예약 내역이 없습니다</h4>
            <p className="text-gray-600 mb-4">조건에 해당하는 예약이 없습니다.</p>
            <button type="button" className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors" disabled={!derivedProductNo} onClick={() => setReserveDateModalOpen(true)}>예약 하기</button>
          </div>
        </div>
      ) : (
        <div ref={containerRef} className="mt-2 border border-gray-200 rounded" style={{ scrollbarGutter: 'stable' }} onMouseLeave={() => setHoveredRowKey(null)}>
          <div ref={topScrollRef} className="overflow-x-scroll overflow-y-hidden h-4" style={{ scrollbarGutter: 'stable' }}>
            <div style={{ width: totalTableWidth, height: 1 }} />
          </div>
          <Table size={density === 'compact' ? 'small' : 'middle'} tableLayout="fixed" bordered scroll={{ x: totalTableWidth, y: bodyHeight }} columns={antdColumns} dataSource={antdData} rowKey={(record) => record.key || (record._isGroup ? `group-${record.groupKey}` : `${record.주문번호 || ''}-${record.예약일 || ''}`)} rowClassName={(record) => { if (record._isGroup) { const isExpanded = expandedRowKeys.includes(`group-${record.groupKey}`); return isExpanded ? 'bg-blue-50' : ''; } else { return 'child-row'; } }} onRow={(record) => ({ onMouseEnter: () => { const key = record && record._isGroup ? `group-${record.groupKey}` : (record.key || `${record.주문번호 || ''}-${record.예약일 || ''}`); setHoveredRowKey(key); }, onMouseLeave: () => setHoveredRowKey(null), })} pagination={{ pageSize, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'], onChange: (_p, newSize) => { if (newSize && newSize !== pageSize) setPageSize(newSize); }, onShowSizeChange: (_c, newSize) => setPageSize(newSize), }} expandable={{ expandedRowKeys, expandIcon: () => null, onExpand: (expanded, record) => { const key = record && record._isGroup ? `group-${record.groupKey}` : record?.key; if (!key) return; setExpandedRowKeys((prev) => { const set = new Set(prev); if (expanded) set.add(key); else set.delete(key); return Array.from(set); }); } }} />
        </div>
      )}

      <GroupInfoDrawer open={!!groupDetail?.saleNo} onClose={() => setGroupDetail(null)} saleNo={groupDetail?.saleNo} title={groupDetail?.groupName} />

      <AvailableDatesModal open={reserveDateModalOpen} onClose={() => setReserveDateModalOpen(false)} productNo={derivedProductNo} productName={derivedProductName} initialProductTypeName={modalProductTypeName} onSelect={({ date, datePriceNo, productTypeName }) => { setSelectedDate(date); setSelectedPriceNo(String(datePriceNo || '')); setSelectedProductTypeName(productTypeName || '투어'); setReserveDateModalOpen(false); setReservationModalOpen(true); }} />

      {String(selectedProductTypeName || '').trim() === '호텔' ? (
        <HotelReservationModal open={reservationModalOpen} onClose={() => setReservationModalOpen(false)} priceNo={selectedPriceNo} datePriceNo={selectedPriceNo} date={selectedDate} productName={derivedProductName} />
      ) : (
        <GeneralReservationModal open={reservationModalOpen} onClose={() => setReservationModalOpen(false)} priceNo={selectedPriceNo} datePriceNo={selectedPriceNo} date={selectedDate} productName={derivedProductName} />
      )}

      {isSettingsOpen && (
        <ProductSettingsModal product={selectedProduct || settingsProduct} onClose={handleCloseModal} onSave={handleSaveSettings} loading={settingsLoading} />
      )}
    </div>
  );
}
